import { find, isEmpty } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import {
  CheckCircleFilledIcon,
  CloseCircleFilledIcon,
  LoadingIcon,
  TimeFilledIcon,
} from 'tdesign-icons-react';
import { MessagePlugin, Steps } from 'tdesign-react';
import { convertTime } from '@/utils/common';
import { setRecordStep, setRecordSubStep } from '@/slices/execRecordSlice';
import { RESULT_STATUS, getExecResultCodeStatus } from '../..';

const { StepItem } = Steps;

export const getStatusClass = (execResultCode) => {
  const code = getExecResultCodeStatus(execResultCode);
  switch (code) {
    case RESULT_STATUS.PROCESSING: // 正在执行
      return 'processing';
    case RESULT_STATUS.ERROR: // 错误
      return 'error';
    case RESULT_STATUS.SUCCESS:
      return 'success';
    case RESULT_STATUS.READY:
      return 'ready';
    default:
      return null;
  }
};

function StepsContainer({ steps, setRecordStep, currentStep, setRecordSubStep, currentSubStep }) {
  const getIcon = (execResultCode) => {
    const result = getExecResultCodeStatus(execResultCode);
    switch (result) {
      case RESULT_STATUS.PROCESSING:
        return <LoadingIcon size='large' style={{ color: 'var(--td-brand-color)' }} />;
      case RESULT_STATUS.ERROR:
        return <CloseCircleFilledIcon size='large' style={{ color: 'var(--td-error-color)' }} />;
      case RESULT_STATUS.SUCCESS:
        return <CheckCircleFilledIcon size='large' style={{ color: 'var(--td-success-color)' }} />;
      case RESULT_STATUS.READY:
        return <TimeFilledIcon size='large' style={{ color: 'var(--td-text-color-secondary)' }} />;
      default:
        break;
    }
  };

  const onChangeStep = (value) => {
    const found = find(steps, { stepId: value });
    // 如果还未开始执行，不允许选中
    if (getExecResultCodeStatus(found.execResultCode) === RESULT_STATUS.READY) {
      MessagePlugin.info('该节点尚未执行');
      return;
    }
    // 有子节点，未选中(点击边界处)
    if (!isEmpty(found.subSteps)) {
      const firstChild = found.subSteps[0];
      setRecordSubStep(firstChild);
      setRecordStep(found);
      return;
    }
    setRecordStep(found);
    setRecordSubStep(null);
  };

  const onChangeSubStep = (items, value, parentId, e) => {
    e.stopPropagation();
    const found = find(items, { keyId: value });
    // 未执行节点，不点击
    if (getExecResultCodeStatus(found.execResultCode) === RESULT_STATUS.READY) {
      MessagePlugin.info('该节点尚未执行');
      return;
    }
    const foundParent = find(steps, { stepId: parentId });
    if (foundParent.stepId !== currentStep?.stepId) {
      // 选中父节点
      setRecordStep(foundParent);
    }
    // 子节点
    setRecordSubStep(found);
  };

  return (
    <Root>
      <Title>执行步骤</Title>
      <Steps current={currentStep?.stepId} onChange={onChangeStep} layout='vertical'>
        {steps.map((item) => (
          <StepItem
            value={item.stepId}
            className={getStatusClass(item.execResultCode)}
            icon={getIcon(item.execResultCode)}
            key={item.stepId}
            title={item.stepName}
            content={`耗时：${convertTime(item.userTime).pretty}`}
          >
            <Extra onClick={(e) => e.stopPropagation()}>
              {!isEmpty(item.subSteps) && (
                <Steps
                  current={currentSubStep?.keyId}
                  onChange={(value, preValue, { e }) =>
                    onChangeSubStep(item.subSteps, value, item.stepId, e)
                  }
                  layout='vertical'
                >
                  {item.subSteps.map((subItem) => (
                    <StepItem
                      key={subItem.keyId}
                      value={subItem.keyId}
                      className={getStatusClass(subItem.execResultCode)}
                      icon={getIcon(subItem.execResultCode)}
                      title={subItem.stepName}
                      content={`耗时：${convertTime(subItem.userTime).pretty}`}
                    />
                  ))}
                </Steps>
              )}
            </Extra>
          </StepItem>
        ))}
      </Steps>
    </Root>
  );
}

export default connect(
  (state) => ({
    steps: state.execRecord.steps,
    currentStep: state.execRecord.currentStep,
    currentSubStep: state.execRecord.currentSubStep,
  }),
  {
    setRecordStep,
    setRecordSubStep,
  },
)(StepsContainer);

const Root = styled.div`
  padding: 16px 20px;
  width: 230px;

  // 保持竖线样式不变
  .t-steps--vertical.t-steps--positive .t-steps-item {
    padding-bottom: var(--td-comp-margin-s);
    &:not(:last-child)::before {
      border-right-width: 1px !important;
      border-right-color: var(--td-text-color-placeholder) !important;
      color: var(--td-text-color-primary) !important;
    }
  }

  .t-steps {
    .t-steps-item__icon {
      margin-right: 4px;
      .t-steps-item__icon--number {
        border-color: transparent;
        background-color: transparent;
      }
    }
    .t-steps-item__title {
      font-size: 13px;
      color: var(--td-text-color-secondary);
      font-weight: normal;
      margin-bottom: 0;
    }
    .t-steps-item__description {
      font-size: 12px;
    }
  }

  .t-steps-item--process {
    & > .t-steps-item__inner {
      & > .t-steps-item__content > .t-steps-item__title {
        color: var(--td-text-color-primary);
        font-weight: bold;
      }
    }
  }

  .success.t-steps-item--process
    > .t-steps-item__inner
    > .t-steps-item-process
    > .t-steps-item__icon--number {
    background-color: var(--td-success-color-2);
  }
  .error.t-steps-item--process
    > .t-steps-item__inner
    > .t-steps-item-process
    > .t-steps-item__icon--number {
    background-color: var(--td-error-color-2);
  }
  .processing.t-steps-item--process
    > .t-steps-item__inner
    > .t-steps-item-process
    > .t-steps-item__icon--number {
    background-color: var(--td-brand-color-2);
  }
`;

const Title = styled.div`
  color: var(--td-text-color-primary);
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 18px;
`;

const Extra = styled.div`
  width: 100%;
`;
